import React, { useState, useEffect, useCallback } from 'react';
import { formatCurrency } from '../utils/formatCurrency';
import { arBasisLine } from '../utils/arBasis';
import './SuccessPage.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// The Customer Success page (docs/slack-pane.md, "The page"): the practice's weekly-report
// numbers, the same entry the Slack pane's "Customer Success (NCR%)" tab reads, through
// the session route GET /api/practices/<id>/customer-success. Counts and dollars only;
// nothing here names a patient. Staff only (App.js keeps 'success' off CLIENT_PAGES).

export function formatPct(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return null;
  return `${Number(value).toFixed(1)}%`;
}

export function formatCount(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
  return Number(value).toLocaleString('en-US');
}

// The "Metric | Value" rows under the tiles, in the tab's order: last week's claims and
// appointments, the bridge rows, each A/R bucket, then the two A/R totals.
export function metricRows(entry) {
  const rows = [
    { key: 'claims', label: 'Claims last week', value: formatCount(entry.claims) },
    { key: 'appointments', label: 'Appointments last week', value: formatCount(entry.appointments) },
  ];
  (entry.bridge_rows || []).forEach((row, i) => {
    rows.push({ key: `bridge-${i}`, label: row.label, value: formatCount(row.total) });
  });
  // At allowed (a practice whose payers allow under half of billed), billed beside it.
  const allowed = entry.ar_basis === 'allowed';
  const withBilled = (amount, billed) =>
    allowed ? `${formatCurrency(amount)} (billed ${formatCurrency(billed)})` : formatCurrency(amount);
  const suffix = allowed ? ' (at allowed)' : '';
  (entry.ar_buckets || []).forEach((bucket) => {
    rows.push({ key: `ar-${bucket.bucket}`, label: `A/R ${bucket.bucket} days${suffix}`, value: withBilled(bucket.amount, bucket.billed) });
  });
  rows.push({ key: 'ar-insurance', label: `A/R insurance${suffix}`, value: withBilled(entry.ar_insurance_total, entry.ar_insurance_billed) });
  // The patient owes the charge: patient A/R is at billed on every basis.
  rows.push({ key: 'ar-patient', label: 'A/R patient', value: formatCurrency(entry.ar_patient_total) });
  return rows;
}

function formatDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function SuccessPage({ selectedPractice }) {
  const practiceId = selectedPractice?.id;
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!practiceId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${API_URL}/api/practices/${practiceId}/customer-success`, { credentials: 'include' });
      if (!resp.ok) {
        let message = `Could not load the weekly numbers (HTTP ${resp.status})`;
        try {
          const body = await resp.json();
          if (body && body.error) message = body.error;
        } catch (e) {
          // not JSON; keep the status message
        }
        throw new Error(message);
      }
      setPayload(await resp.json());
    } catch (e) {
      setPayload(null);
      setError(e.message || 'Could not load the weekly numbers');
    } finally {
      setLoading(false);
    }
  }, [practiceId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!selectedPractice) {
    return (
      <div className="success-page">
        <div className="empty-state">
          <h3>No practice selected</h3>
          <p>Choose a practice from the sidebar to view its weekly numbers.</p>
        </div>
      </div>
    );
  }

  const entry = payload?.practices?.[0] || null;
  const headlinePct = entry && !entry.error ? formatPct(entry.ncr_pct) : null;
  const closedPct = entry && !entry.error ? formatPct(entry.ncr_if_all_closed_pct) : null;

  return (
    <div className="success-page">
      <div className="success-header">
        <div>
          <h1 className="page-title">Customer Success</h1>
          {payload && (
            <p className="success-subtitle">
              As of {formatDate(payload.as_of)}, week of {formatDate(payload.week_start)}. The weekly report's own numbers.
            </p>
          )}
        </div>
        <button className="success-refresh" onClick={load} disabled={loading}>Refresh</button>
      </div>

      {error && (
        <div className="success-message error">
          <span>{error}</span>
          <button className="message-close" onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading the weekly numbers...</p>
        </div>
      ) : entry && entry.error ? (
        <div className="success-uncomputed">
          <h3>These numbers could not be computed</h3>
          <p>
            The weekly report could not be built for this practice.
            {entry.error_id && <> Error id <code>{entry.error_id}</code>.</>}
          </p>
        </div>
      ) : entry ? (
        <>
          {entry.note && <div className="success-note">{entry.note}</div>}

          <div className="success-tiles">
            <div className="success-tile">
              <div className="success-tile-label">NCR</div>
              <div className="success-tile-value">{headlinePct || '-'}</div>
              <div className="success-tile-hint">{entry.ncr_label ? `Window: ${entry.ncr_label}` : 'No window reported'}</div>
            </div>
            <div className="success-tile">
              <div className="success-tile-label">NCR if all closed</div>
              <div className={`success-tile-value${closedPct ? '' : ' muted'}`}>{closedPct || 'not reported'}</div>
              <div className="success-tile-hint">If every open block closed</div>
            </div>
            <div className="success-tile">
              <div className="success-tile-label">MTD collections</div>
              <div className="success-tile-value">{formatCurrency(entry.mtd_collections)}</div>
              <div className="success-tile-hint">Payments received this month</div>
            </div>
          </div>

          <div className="success-section">
            <h2>Last week and A/R</h2>
            <table className="success-table">
              <thead>
                <tr>
                  <th>Metric</th>
                  <th className="num">Value</th>
                </tr>
              </thead>
              <tbody>
                {metricRows(entry).map((row) => (
                  <tr key={row.key}>
                    <td>{row.label}</td>
                    <td className="num">{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {entry.ar_ratio && (
              <p className="success-data-quality" data-testid="success-ar-basis">
                {arBasisLine(entry.ar_ratio)}
                {entry.ar_basis === 'allowed' && ' Patient balances inside the buckets stay at billed.'}
              </p>
            )}
          </div>

          {entry.gap_to_100 && entry.gap_to_100.length > 0 ? (
            <div className="success-section">
              <h2>Gap from "if all closed" to 100%</h2>
              <table className="success-table" data-testid="success-gap-to-100">
                <thead>
                  <tr>
                    <th>Remaining gap</th>
                    <th className="num">Dollars</th>
                    <th className="num">Points</th>
                    <th className="num">Claims</th>
                  </tr>
                </thead>
                <tbody>
                  {entry.gap_to_100.map((g) => (
                    <tr key={g.key}>
                      <td>{g.label}</td>
                      <td className="num">{formatCurrency(g.dollars)}</td>
                      <td className="num">{g.points === null || g.points === undefined ? '-' : Number(g.points).toFixed(1)}</td>
                      <td className="num">{formatCount(g.claims)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          <div className="success-section">
            <h2>Main issues</h2>
            {entry.main_issues && entry.main_issues.length > 0 ? (
              <table className="success-table">
                <thead>
                  <tr>
                    <th>Issue</th>
                    <th className="num">Dollars</th>
                    <th className="num">Points</th>
                    <th className="num">Claims</th>
                    <th>Owner</th>
                  </tr>
                </thead>
                <tbody>
                  {entry.main_issues.map((issue, i) => (
                    <tr key={`${issue.label}-${i}`}>
                      <td>{issue.label}</td>
                      <td className="num">{formatCurrency(issue.dollars)}</td>
                      <td className="num">{issue.points === null || issue.points === undefined ? '-' : Number(issue.points).toFixed(1)}</td>
                      <td className="num">{formatCount(issue.claims)}</td>
                      <td>{issue.owner || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="success-empty">No main issues reported for this week.</p>
            )}
          </div>
        </>
      ) : !error ? (
        <div className="empty-state">
          <h3>No numbers yet</h3>
          <p>The weekly report has nothing for this practice.</p>
        </div>
      ) : null}
    </div>
  );
}

export default SuccessPage;
